import { useEffect, useState } from "react";
import type { RoleDefinition } from "../../app/roles";
import { ImpactTrace } from "../../components/ImpactTrace";
import { StatusPill } from "../../components/StatusPill";
import { activeIncident, responseOptions, scenarioEvidence } from "../../data/seed";
import type { ResponseOption } from "../../domain";
import { DepthBrief, PerspectiveFieldGuide, defineDepthContent, useDepth, useDepthValue } from "../../presentation";
import { useScenario } from "../../simulation/ScenarioContext";
import { SafetyHumanGate } from "./SafetyHumanGate";

type IncidentWorkspaceProps = {
  role: RoleDefinition;
  onOpenTrace: () => void;
  onOpenReports: () => void;
};

const incidentDepthContent = defineDepthContent({
  overview: {
    calmSummary: "The controlled incident is ready. Starting it will show impact, ownership, and the next safe response.",
    activeSummary: "Wireless and desk-phone reliability is reduced in one academic building.",
    title: "Impact and next action",
    signals: ["North Academic is affected", "Technology Operations owns the response", "Review evidence before acting"] as const,
  },
  operations: {
    calmSummary: "Start the deterministic scenario to investigate evidence, compare consequences, and create receipts.",
    activeSummary: activeIncident.workingTheory,
    title: "Evidence-backed investigation",
    signals: ["Correlated alerts and change evidence", "Two-record decision gate", "Response consequence and receipt"] as const,
  },
  architecture: {
    calmSummary: "Activate the scenario to inspect the record graph and deterministic transition inputs.",
    activeSummary: "Correlated service, device, interface, VLAN, assignment, and person records resolve through explicit identifiers.",
    title: "Correlation and provenance",
    signals: ["INC-2048 and CHG-9012 anchors", "CR-SW-04 to TEL-0318 relationship path", "Rule-based transition and evidence sources"] as const,
  },
});

export function IncidentWorkspace({ role, onOpenTrace, onOpenReports }: IncidentWorkspaceProps) {
  const {
    decisionId,
    humanDecision,
    linkEvidence,
    linkedEvidenceIds,
    receipts,
    recordArtifact,
    recoveryPercent,
    recoveryVerification,
    remainingUsers,
    reviseResponse,
    selectedDecision,
    stage,
    startScenario,
  } = useScenario();
  const { depth } = useDepth();
  const lens = useDepthValue(incidentDepthContent);
  const [pendingDecision, setPendingDecision] = useState<ResponseOption["id"] | null>(null);
  const canDecide = role.canManageIncidents;
  const evidenceReady = linkedEvidenceIds.length >= 2;
  const awaitingVerification = humanDecision?.decision === "approved"
    && !(
      recoveryVerification?.serviceRecovered
      && recoveryVerification.endpointReregistered
    );

  useEffect(() => {
    if (decisionId || humanDecision) setPendingDecision(null);
  }, [decisionId, humanDecision]);

  const stageTone =
    stage === "recovered" ? "healthy" : stage === "monitoring" || stage === "identified" ? "warning" : "critical";

  if (stage === "calm") {
    return (
      <section aria-labelledby="incident-title">
        <div className="page-heading">
          <div>
            <p className="eyebrow">Incident workspace</p>
            <h1 id="incident-title">INC-2048 is ready to simulate</h1>
            <p>{lens.calmSummary}</p>
          </div>
          <button className="button button--primary" onClick={() => startScenario(role.label)} type="button">
            Start controlled scenario
          </button>
        </div>
        <DepthBrief summary={lens.calmSummary} signals={lens.signals} title={lens.title} />
        <PerspectiveFieldGuide surface="incidents" />
        <article className="panel preflight-panel">
          <div>
            <span className="field-label">
              {depth === "overview" ? "What this story demonstrates" : depth === "operations" ? "Scenario boundary" : "Record boundary"}
            </span>
            <h2>
              {depth === "overview"
                ? "A safe, guided service-recovery story"
                : depth === "operations"
                  ? "Local demonstration data only"
                  : "Deterministic records with explicit identifiers"}
            </h2>
            <p>
              {depth === "overview"
                ? "Start the scenario to see who is affected, who owns the response, and which action restores service."
                : depth === "operations"
                  ? "No campus system, Microsoft tenant, or external service is connected."
                  : "INC-2048, CHG-9012, CR-SW-04, TEL-0318, and their edges are generated from the local seed contract."}
            </p>
          </div>
          <ul>
            {depth === "overview" && <><li>One building and 183 people</li><li>Clear owner and next action</li><li>Resettable demonstration</li></>}
            {depth === "operations" && <><li>Deterministic evidence and consequences</li><li>Resettable without losing application stability</li><li>Role-aware decisions and append-only receipts</li></>}
            {depth === "architecture" && <><li>Stable record and evidence IDs</li><li>Typed service-to-person relationships</li><li>Rule-based transitions and provenance</li></>}
          </ul>
        </article>
      </section>
    );
  }

  return (
    <section aria-labelledby="incident-title">
      <div className="incident-hero">
        <div>
          <div className="incident-hero__meta">
            <StatusPill label={activeIncident.severity} tone="critical" />
            <StatusPill label={stage} tone={stageTone} />
            <span>Owner · {activeIncident.owner}</span>
          </div>
          <p className="eyebrow">INC-2048 · North Academic Center</p>
          <h1 id="incident-title">{activeIncident.title}</h1>
          <p>{lens.activeSummary}</p>
        </div>
        <div
          className="recovery-gauge"
          aria-label={`${recoveryPercent}% ${awaitingVerification ? "projected recovery" : "service recovery"}`}
        >
          <span>{awaitingVerification ? "Projected recovery" : "Service recovery"}</span>
          <strong>{recoveryPercent}%</strong>
          <div><i style={{ width: `${recoveryPercent}%` }} /></div>
          <small>
            {remainingUsers} users {awaitingVerification ? "projected to remain affected" : "remain affected"}
          </small>
        </div>
      </div>

      <DepthBrief summary={lens.activeSummary} signals={lens.signals} title={lens.title} />
      <PerspectiveFieldGuide surface="incidents" />

      {depth === "overview" && (
        <section className="depth-surface overview-action-grid" aria-label="Incident outcome summary">
          <article className="overview-action-card">
            <span>Current impact</span>
            <strong>{remainingUsers} people remain affected</strong>
            <p>Staff wireless and desk-phone registration are unreliable in North Academic Center.</p>
          </article>
          <article className="overview-action-card">
            <span>Response owner</span>
            <strong>{activeIncident.owner}</strong>
            <p>{linkedEvidenceIds.length} evidence records are linked and {receipts.length} accountable receipts exist.</p>
          </article>
          <article className="overview-action-card">
            <span>Next safe action</span>
            <strong>{evidenceReady ? "Compare response consequences" : "Link two supporting records"}</strong>
            <p>The working controls remain below; technical topology is condensed in this view.</p>
          </article>
        </section>
      )}

      {depth === "architecture" && (
        <section className="depth-surface architecture-record-grid" aria-label="Incident provenance map">
          <article className="architecture-record-card">
            <span>Correlation root</span><strong>INC-2048 + CHG-9012</strong>
            <p>The transition window correlates the configuration change with service degradation.</p>
          </article>
          <article className="architecture-record-card">
            <span>Infrastructure path</span><strong>CR-SW-04 → VLAN-120</strong>
            <p>Explicit interface and termination edges connect intended state to endpoint impact.</p>
          </article>
          <article className="architecture-record-card">
            <span>Impact edge</span><strong>TEL-0318 → ASN-1182</strong>
            <p>The assignment record resolves a permitted asset record to its affected user.</p>
          </article>
        </section>
      )}

      <div className="incident-layout">
        <div className="incident-main">
          {depth !== "overview" && <article className="panel">
            <header className="panel__header">
              <div>
                <p className="eyebrow">Impact trace</p>
                <h2>From service alert to assigned user</h2>
              </div>
              <button className="text-button" onClick={onOpenTrace} type="button">Open full trace</button>
            </header>
            <ImpactTrace compact />
          </article>}

          <article className="panel">
            <header className="panel__header">
              <div>
                <p className="eyebrow">Evidence workbench</p>
                <h2>Build the incident theory</h2>
              </div>
              <span>{linkedEvidenceIds.length}/{scenarioEvidence.length} linked</span>
            </header>
            <div className="evidence-grid">
              {scenarioEvidence.map((evidence) => {
                const linked = linkedEvidenceIds.includes(evidence.id);
                return (
                  <article className={linked ? "evidence-card is-linked" : "evidence-card"} key={evidence.id}>
                    <span>
                      {depth === "architecture" ? `${evidence.id} · ` : ""}
                      {evidence.source} · {evidence.observedAt}
                    </span>
                    <strong>{evidence.label}</strong>
                    <p>{evidence.supports}</p>
                    <div>
                      <span className={`confidence confidence--${evidence.confidence}`}>
                        {evidence.confidence} confidence
                      </span>
                      <button
                        className="text-button"
                        disabled={linked}
                        onClick={() => linkEvidence(evidence.id, role.label)}
                        type="button"
                      >
                        {linked ? "Linked" : "Add to incident"}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </article>

          <article className="panel response-panel">
            <header className="panel__header">
              <div>
                <p className="eyebrow">Response decision</p>
                <h2>Compare consequences before acting</h2>
              </div>
              <span>{evidenceReady ? "Evidence gate cleared" : "Two evidence records required"}</span>
            </header>
            {selectedDecision ? (
              <div className="decision-result">
                <div>
                  <span className="field-label">Approved branch · verification required</span>
                  <h3>{selectedDecision.label}</h3>
                  <p>{selectedDecision.result}</p>
                </div>
                <dl>
                  <div><dt>Recovery</dt><dd>{selectedDecision.recoveryPercent}%</dd></div>
                  <div><dt>Time</dt><dd>{selectedDecision.minutesToRestore} min</dd></div>
                  <div><dt>Confidence</dt><dd>{selectedDecision.confidence}%</dd></div>
                </dl>
                <button className="button button--secondary" onClick={() => reviseResponse(role.label)} type="button">
                  Revise response
                </button>
              </div>
            ) : (
              <>
                <div className="response-options">
                  {responseOptions.map((option) => (
                    <button
                      aria-pressed={pendingDecision === option.id}
                      className={pendingDecision === option.id ? "response-option is-selected" : "response-option"}
                      disabled={!canDecide || !evidenceReady}
                      key={option.id}
                      onClick={() => setPendingDecision(option.id)}
                      type="button"
                    >
                      <span>{option.recoveryPercent}% projected recovery</span>
                      <strong>{option.label}</strong>
                      <p>{option.summary}</p>
                      <small>{option.risk}</small>
                    </button>
                  ))}
                </div>
                {!canDecide && (
                  <p className="blocked-explanation">
                    The {role.label} role may inspect and link evidence but must escalate response approval to Technical II.
                  </p>
                )}
                {canDecide && !evidenceReady && (
                  <p className="blocked-explanation">
                    Link two evidence records before confirming a network response.
                  </p>
                )}
                {pendingDecision && (
                  <div className="decision-review" role="region" aria-label="Response confirmation">
                    <div>
                      <span className="field-label">Proposed for governed review</span>
                      <strong>{responseOptions.find((option) => option.id === pendingDecision)?.label}</strong>
                      <p>
                        Selection does not execute this response. Complete the
                        safety checks and human decision below.
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
            <SafetyHumanGate
              onDecisionRecorded={() => setPendingDecision(null)}
              pendingDecision={pendingDecision}
              role={role}
            />
          </article>

          {selectedDecision && (
            <article className="panel communication-panel">
              <header className="panel__header">
                <div>
                  <p className="eyebrow">Communication artifacts</p>
                  <h2>Recovery messaging and documentation</h2>
                </div>
                <span>Preview environment</span>
              </header>
              <div className="artifact-actions">
                <button onClick={() => recordArtifact("teams", role.label)} type="button">
                  <span>Microsoft Teams preview</span>
                  <strong>Prepare restoration update</strong>
                  <small>No message will be delivered</small>
                </button>
                <button onClick={onOpenReports} type="button">
                  <span>Incident report</span>
                  <strong>Review current report</strong>
                  <small>Generated from live scenario state</small>
                </button>
              </div>
            </article>
          )}
        </div>

        {depth === "operations" && <aside className="incident-rail">
          <article className="panel">
            <header className="panel__header">
              <div><p className="eyebrow">Decision support</p><h2>Simulated analysis</h2></div>
              <StatusPill label="Evidence-backed" tone="healthy" />
            </header>
            <p className="analysis-copy">
              Rollback currently has the strongest support because the configuration change, error timing, and endpoint
              registration loss align. Missing evidence: a post-rollback verification sample.
            </p>
            <dl className="analysis-facts">
              <div><dt>Supporting</dt><dd>{linkedEvidenceIds.length} linked records</dd></div>
              <div><dt>Opposing</dt><dd>No hardware fault found</dd></div>
              <div><dt>Boundary</dt><dd>Rule-based local simulation</dd></div>
            </dl>
          </article>

          <article className="panel receipt-panel">
            <header className="panel__header">
              <div><p className="eyebrow">Incident timeline</p><h2>Receipts</h2></div>
              <span>{receipts.length}</span>
            </header>
            {receipts.length ? (
              <ol className="receipt-timeline">
                {receipts.map((receipt) => (
                  <li key={receipt.id}>
                    <time>{receipt.occurredAt}</time>
                    <div>
                      <strong>{receipt.action}</strong>
                      <span>{receipt.actor} · {receipt.recordId}</span>
                      <p>{receipt.outcome}</p>
                    </div>
                  </li>
                ))}
              </ol>
            ) : <p className="empty-copy">The first scenario action will create a receipt.</p>}
          </article>
        </aside>}

        {depth === "architecture" && (
          <aside className="incident-rail">
            <article className="panel">
              <header className="panel__header">
                <div><p className="eyebrow">Transition inputs</p><h2>Deterministic boundary</h2></div>
                <StatusPill label="Local" tone="healthy" />
              </header>
              <dl className="analysis-facts">
                <div><dt>Incident</dt><dd>INC-2048</dd></div>
                <div><dt>Change</dt><dd>CHG-9012</dd></div>
                <div><dt>Decision gate</dt><dd>2 linked evidence records</dd></div>
                <div><dt>Receipt stream</dt><dd>{receipts.length} append-only events</dd></div>
              </dl>
            </article>
            <article className="panel">
              <header className="panel__header">
                <div><p className="eyebrow">Evidence provenance</p><h2>Source record boundary</h2></div>
              </header>
              <p className="analysis-copy">
                Evidence objects are local simulation records. Their identifiers, observed times, and source labels are
                preserved when linked; no external system is queried.
              </p>
            </article>
          </aside>
        )}
      </div>
    </section>
  );
}
